import { brand } from '@metorial/brand';
import { Paths } from '@metorial/frontend-config';
import { PageHeader } from '@metorial/layout';
import { Text, theme, Title } from '@metorial/ui';
import {
  RiArrowRightSLine,
  RiBarChartBoxLine,
  RiBellLine,
  RiBuilding2Line,
  RiEyeLine,
  RiFingerprintLine,
  RiGitBranchLine,
  RiKey2Line,
  RiLayoutGridLine,
  RiServerLine,
  RiSettings3Line,
  RiShieldKeyholeLine,
  RiShieldLine,
  RiSparkling2Line,
  RiTeamLine,
  RiUserSettingsLine
} from '@remixicon/react';
import React from 'react';
import { Link } from 'react-router-dom';
import { styled } from 'styled-components';
import { useShellScope } from '../../../shell';

// Settings home (cloud-spec §7): sections of icon cards, each linking to a
// settings page. BlackOS has Usage instead of Billing, and no Outposts or
// Support Access.

let Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 28px;
`;

// 3 columns of icon tiles, like the cloud; fewer when the panel is narrow.
let Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

let Card = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 14px 14px 16px;
  border: 1px solid ${theme.colors.gray400};
  border-radius: 10px;
  background: ${theme.colors.background};
  color: inherit;
  text-decoration: none;
  transition: none;

  &:hover,
  &:focus-visible {
    border-color: ${theme.colors.gray600};
    background: ${theme.colors.gray100};
  }
`;

let IconBox = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${theme.colors.gray200};
  color: ${theme.colors.gray900};

  svg {
    width: 17px;
    height: 17px;
  }
`;

let CardBody = styled.span`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
`;

let Arrow = styled.span`
  display: flex;
  align-self: center;
  color: ${theme.colors.gray600};
`;

type CardLink = {
  title: string;
  description: string;
  to: string;
  icon: React.ReactNode;
};

export let SettingsHomePage = () => {
  let scope = useShellScope();
  let o = scope.organization;
  let p = [scope.organization, scope.project, scope.instance] as const;
  let ps = Paths.organization.projectSettings;
  let inst = Paths.organization.instance;

  let sections: { title: string; links: CardLink[] }[] = [
    {
      title: 'Organization',
      links: [
        {
          title: 'General',
          description: 'Update your organization name, image, and general information.',
          to: Paths.organization.general(o),
          icon: <RiBuilding2Line />
        },
        {
          title: 'Notifications',
          description: 'Choose which product and organization updates you receive.',
          to: Paths.organization.notifications(o),
          icon: <RiBellLine />
        },
        {
          title: 'Usage',
          description: 'Review sessions, tool calls, and portal users across your organization.',
          to: Paths.organization.usage(o),
          icon: <RiBarChartBoxLine />
        },
        {
          title: 'Team',
          description: 'Manage organization members, invitations, and teams.',
          to: Paths.organization.members(o),
          icon: <RiTeamLine />
        },
        {
          title: 'Access',
          description: 'Manage policies, roles, applications, and machine access.',
          to: Paths.organization.policies(o),
          icon: <RiShieldKeyholeLine />
        }
      ]
    },
    {
      title: 'Project',
      links: [
        {
          title: 'General',
          description: 'Manage the project profile and branding.',
          to: inst(...p),
          icon: <RiSettings3Line />
        },
        {
          title: 'Security',
          description: 'Configure log retention.',
          to: ps.retention(o, scope.project),
          icon: <RiShieldLine />
        },
        {
          title: 'Source Control',
          description: 'Connect source repositories and SCM providers.',
          to: ps.scm(o, scope.project),
          icon: <RiGitBranchLine />
        }
      ]
    },
    {
      title: 'Integrations and MCP',
      links: [
        {
          title: 'MCP Configuration',
          description:
            'Configure MCP authentication, tool calling, sessionless MCP, and delegation.',
          to: ps.authConfig(o, scope.project),
          icon: <RiServerLine />
        },
        {
          title: 'Provider Auth',
          description: 'Manage authentication credentials across your providers.',
          to: inst.providerAuthCredentials(...p),
          icon: <RiKey2Line />
        },
        {
          title: 'Identity',
          description: 'Manage identity delegation configuration.',
          to: inst.identity.delegationConfigs(...p),
          icon: <RiFingerprintLine />
        },
        {
          title: 'Prompt Monitoring',
          description: 'Review alerts, monitors, and ProtoGuard filters.',
          to: inst.alerts(...p),
          icon: <RiEyeLine />
        }
      ]
    },
    {
      title: 'Workforce',
      links: [
        {
          title: 'Portals',
          description:
            'Create and manage branded portals for employees, partners, and customers.',
          to: inst.portals(...p),
          icon: <RiLayoutGridLine />
        },
        {
          title: 'Settings',
          description: 'Configure automatic portal membership.',
          to: inst.portalsSettings(...p),
          icon: <RiUserSettingsLine />
        }
      ]
    },
    {
      title: 'Skills',
      links: [
        {
          title: 'General',
          description: 'Configure default skill execution policies.',
          to: inst.skillsGeneral(...p),
          icon: <RiSparkling2Line />
        }
      ]
    }
  ];

  return (
    <>
      <PageHeader
        top={
          <Text size="2" weight="strong" color="gray600">
            {o?.name}
          </Text>
        }
        title="Settings"
        description={`Manage your ${brand.name} settings, including team members, usage, and integrations.`}
      />

      {sections.map(section => (
        <Section key={section.title}>
          <Title as="h2" size="3" weight="strong">
            {section.title}
          </Title>

          <Grid>
            {section.links.map(link => (
              <Card key={link.title} to={link.to}>
                <IconBox>{link.icon}</IconBox>
                <CardBody>
                  <Text size="2" weight="strong">
                    {link.title}
                  </Text>
                  <Text size="1" color="gray600">
                    {link.description}
                  </Text>
                </CardBody>
                <Arrow>
                  <RiArrowRightSLine size={18} />
                </Arrow>
              </Card>
            ))}
          </Grid>
        </Section>
      ))}
    </>
  );
};
